import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { appConfig } from '../config/appConfig';
import { useContent } from '../context/ContentContext';
import './Layout.css';

export function Layout() {
  const { data } = useContent();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu when clicking outside or pressing escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <div className="app-shell">
      <header className="site-header">
        <div className="header-inner">
          <Link to="/" className="brand">
            <img
              src="/images/democracy-on-the-ballot-favicon.svg"
              alt=""
              className="brand-logo"
              aria-hidden="true"
            />
            <span className="brand-lockup" aria-label={appConfig.siteName}>
              <span className="brand-democracy">Democracy</span>
              <span className="brand-middle">
                <span aria-hidden="true"></span>
                <span>On The</span>
                <span aria-hidden="true"></span>
              </span>
              <span className="brand-ballot">Ballot</span>
              {appConfig.tagline && <span className="brand-tagline">{appConfig.tagline}</span>}
            </span>
          </Link>
          <nav className="main-nav desktop-nav" aria-label="Primary">
            <NavLink to="/" end className={({ isActive }) => (isActive ? 'active' : '')}>
              Home
            </NavLink>
            <NavLink to="/explore" className={({ isActive }) => (isActive ? 'active' : '')}>
              Issues
            </NavLink>
            <NavLink to="/vote" className={({ isActive }) => (isActive ? 'active' : '')}>
              Voting Resources
            </NavLink>
            <NavLink to="/clarity" className={({ isActive }) => (isActive ? 'active' : '')}>
              Clarity
            </NavLink>
            {appConfig.enableSearch && (
              <NavLink to="/search" className={({ isActive }) => (isActive ? 'active' : '')}>
                Search
              </NavLink>
            )}
          </nav>

          <button
            className="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-expanded={isMobileMenuOpen}
            aria-label="Toggle navigation menu"
          >
            <span className={`hamburger ${isMobileMenuOpen ? 'active' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>

        <div className={`mobile-nav-overlay ${isMobileMenuOpen ? 'active' : ''}`}>
          <nav className="mobile-nav" aria-label="Primary">
            <button
              type="button"
              className="mobile-nav-close"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Close navigation menu"
            >
              Close
            </button>
            <NavLink
              to="/"
              end
              className={({ isActive }) => (isActive ? 'active' : '')}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Home
            </NavLink>
            <NavLink
              to="/explore"
              className={({ isActive }) => (isActive ? 'active' : '')}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Issues
            </NavLink>
            <NavLink
              to="/vote"
              className={({ isActive }) => (isActive ? 'active' : '')}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Voting Resources
            </NavLink>
            <NavLink
              to="/clarity"
              className={({ isActive }) => (isActive ? 'active' : '')}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Clarity
            </NavLink>
            {appConfig.enableSearch && (
              <NavLink
                to="/search"
                className={({ isActive }) => (isActive ? 'active' : '')}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Search
              </NavLink>
            )}
          </nav>
        </div>
      </header>

      <main className="site-main">
        <Outlet />
      </main>

      <footer className="site-footer">
        <p>{appConfig.footerNote}</p>
        {appConfig.contactEmail && (
          <p className="footer-contact">
            Contact:{' '}
            <a href={`mailto:${appConfig.contactEmail}`}>{appConfig.contactEmail}</a>
          </p>
        )}
        {data && (
          <p className="footer-meta">
            Schema {data.schemaVersion} · Updated {data.createdAt.slice(0, 10)}
          </p>
        )}
      </footer>
    </div>
  );
}
